// =============================================================================
// FILE: packages/renderer/src/components/auth/EntraDeviceCodeModal.tsx
// PURPOSE: Modal shown while the Microsoft Entra ID device-code flow is in
//          progress. Displays the verification URI + user code with a
//          copy-to-clipboard button.
// =============================================================================

import { useEffect, useState } from 'react';
import { Copy, ExternalLink, X } from 'lucide-react';
import { IPC } from '@bim/shared/constants/ipc.constants.js';

export interface DeviceCodeChallenge {
  userCode: string;
  verificationUri: string;
  expiresInSeconds: number;
  message: string;
}

interface Props {
  challenge: DeviceCodeChallenge | null;
  onClose: () => void;
}

export function EntraDeviceCodeModal({ challenge, onClose }: Props): JSX.Element | null {
  const [copied, setCopied] = useState(false);
  const [remaining, setRemaining] = useState(challenge?.expiresInSeconds ?? 0);

  useEffect(() => {
    if (!challenge) return;
    setRemaining(challenge.expiresInSeconds);
    const t = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [challenge]);

  if (!challenge) return null;

  const copyCode = (): void => {
    void navigator.clipboard.writeText(challenge.userCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div
      data-testid="entra-device-code-modal"
      data-channel={IPC.PUSH_ENTRA_DEVICE_CODE}
      className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm"
    >
      <div className="panel max-w-md w-[92vw] p-7 relative">
        <button
          data-testid="entra-modal-close"
          onClick={onClose}
          className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-sm text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/60 transition"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>

        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Microsoft Entra ID</div>
        <h3 className="text-xl font-semibold tracking-tight mb-2">Complete sign-in in your browser</h3>
        <p className="text-[12px] text-zinc-500 leading-relaxed mb-5">
          Open the verification URL below, paste the user code, and approve the request.
          This window will close automatically when authentication completes.
        </p>

        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Step 1 · Verification URL</div>
          <a
            href={challenge.verificationUri}
            target="_blank"
            rel="noreferrer"
            data-testid="entra-verification-url"
            className="flex items-center gap-2 px-3 py-2 rounded-sm border border-zinc-800 bg-zinc-900/60 text-[12px] text-amber-300 hover:border-amber-300/40 transition"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
            {challenge.verificationUri}
          </a>
        </div>

        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Step 2 · User code</div>
          <div className="flex items-center gap-2">
            <div data-testid="entra-user-code" className="flex-1 px-3 py-3 rounded-sm border border-zinc-800 bg-zinc-900/60 font-mono text-2xl tracking-[0.4em] text-zinc-100 text-center">
              {challenge.userCode}
            </div>
            <button
              data-testid="entra-copy-code"
              onClick={copyCode}
              className="h-12 w-12 grid place-items-center rounded-sm border border-zinc-800 hover:border-zinc-700 text-zinc-300 transition"
              title="Copy user code"
            >
              <Copy className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
          {copied && <div className="text-[11px] text-emerald-400 mt-1 font-mono">Copied to clipboard</div>}
        </div>

        <div className="flex items-baseline justify-between text-[11px] font-mono text-zinc-500">
          <span>Expires in</span>
          <span data-testid="entra-countdown" className={remaining < 60 ? 'text-rose-400' : 'text-zinc-300'}>
            {mm}:{ss}
          </span>
        </div>
      </div>
    </div>
  );
}
